import { ThemeOptions } from '@mui/material'

/**
 * comstruct Principal Color
 * @category Themes
 */
const primaryColor = '#2F6879'

/**
 * comstruct Default Theme Options
 * @remarks Use the default theme options as a base. By default, the theme mode is light.
 * @category Themes
 */
export const defaultThemeOptions: ThemeOptions = {
  shape: {
    borderRadius: 4,
  },
  palette: {
    mode: 'light',
    primary: {
      main: primaryColor,
      contrastText: '#ffffff',
    },
    action: {
      hover: '#F0F7F9',
      active: primaryColor,
      focus: primaryColor,
      selected: primaryColor,
    },
    secondary: {
      main: '#FFCF4A',
      contrastText: '#000000',
    },
    success: {
      main: '#60D394',
    },
    info: {
      main: '#D5E1E4',
    },
    error: {
      main: '#931621',
      light: '#EFA9A9',
    },
  },
  typography: {
    allVariants: {
      fontFamily: "'Open Sans', sans-serif",
    },
    fontFamily: "'Open Sans', sans-serif",
    h2: {
      fontSize: '1.5rem',
      fontWeight: 900,
      fontFamily: "'Montserrat', sans-serif",
    },
    h1: {
      fontSize: '2rem',
      fontWeight: 800,
      fontFamily: "'Montserrat', sans-serif",
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Montserrat', sans-serif",
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Montserrat', sans-serif",
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 800,
      fontFamily: "'Montserrat', sans-serif",
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
      fontFamily: "'Montserrat', sans-serif",
    },
    subtitle1: {
      textTransform: 'uppercase',
      color: primaryColor,
      fontSize: '1rem',
      fontFamily: "'Montserrat', sans-serif",
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {},
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: '1px 2px',
          my: '10px',
          border: '1px solid #E5E7EB',
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          overflow: 'hidden',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiChip-root': {
            color: 'white',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: primaryColor,
          '&:hover': {
            color: primaryColor,
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F0F7F9',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F0F7F9',
          },
          '&.Mui-selected': {
            backgroundColor: '#F0F7F9',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#bdbdbd',
        },
        barColorPrimary: {
          backgroundColor: '#757575',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          shrink: true,
        },
        size: 'small',
      },
    },
  },
}

/**
 * comstruct Status Color
 * @category Themes
 */
export interface StatusColor {
  /** Status color for DEFAULT */
  DEFAULT: string
  /** Status color for SUCCESS */
  SUCCESS: string
  /** Status color for ERROR */
  ERROR: string
  /** Status color for INFO */
  INFO: string
  /** Status color for WARNING */
  WARNING: string
}

/**
 * comstruct Status Color Type
 * @category Themes
 */
export type StatusColorType = keyof StatusColor
